import {Space} from 'antd'
import {getId, getType} from 'apis/model/base'
import DrawMenu from 'components/drawer/DrawMenu'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext, useMemo} from 'react'
import {IoTrashOutline} from 'react-icons/io5'
import CustomButton from 'views/AppLocals/custom/CustomButton'
import {NullAsDefault, renderElse, renderIf} from 'views/Shared'
import HostContext, {HostActionTypes} from '../HostContext'
import renderHostItem from './renderHostItem'
import renderHostRequest from './renderHostRequest'

const RejectedItem = ({owner, host_id}) => {
    const t = useTranslate()
    const {handleAction} = useContext(HostContext)

    const [id, prop] = [getId(owner), getType(owner)]

    return renderIf(
        _.isFunction(handleAction),
        <Space direction="horizontal">
            <CustomButton
                size={'small'}
                onClick={() => {
                    handleAction(HostActionTypes.RESEND, {id, prop, host_id})
                }}
                label="resend"
                color="primary" variant="filled"
                textSize="text-sm md:text-xs"
            />
            <CustomButton
                size={'small'}
                ghost={true}
                onClick={() => {
                    handleAction(HostActionTypes.CANCEL, {
                        id,
                        prop,
                        host_id,
                    })
                }}
                label="cancel request"
                color="default" variant="text"
                textSize="text-sm md:text-xs"
            />
        </Space>
    )
}

const PendingItem = ({owner, status, host_id}) => {
    const t = useTranslate()

    const {handleAction} = useContext(HostContext)

    const [id, prop] = [getId(owner), getType(owner)]

    return renderIf(
        _.isFunction(handleAction),
        <Space direction="horizontal">
            {Number(status) === -9 && (
                <CustomButton
                    size={'small'}
                    ghost={true}
                    onClick={() => {
                        handleAction(HostActionTypes.RESEND, {id, prop, host_id})
                    }}
                    label="resend"
                    color="primary" variant="filled"
                    textSize="text-sm md:text-xs"
                />
            )}
            <CustomButton
                size={'small'}
                ghost={true}
                onClick={() => {
                    handleAction(HostActionTypes.CANCEL, {
                        id,
                        prop,
                        host_id,
                    })
                }}
                label="cancel request"
                color="default" variant="text"
                textSize="text-sm md:text-xs"
            />
        </Space>
    )
}

const OwnerPendingItem = ({owner, status, host_id}) => {
    const t = useTranslate()

    const {handleAction} = useContext(HostContext)

    return renderIf(
        _.isFunction(handleAction),
        <Space direction="horizontal">
            <CustomButton
                size={'small'}
                ghost={true}
                onClick={() => {
                    handleAction(HostActionTypes.APPROVE, {
                        host_id,
                    })
                }}
                label="approve"
                color="primary" variant="filled"
                textSize="text-sm md:text-xs"
            />
            <CustomButton
                size={'small'}
                ghost={true}
                onClick={() => {
                    handleAction(HostActionTypes.DECLINE, {
                        host_id,
                    })
                }}
                label="reject"
                color="default" variant="text"
                textSize="text-sm md:text-xs"
            />
        </Space>
    )
}

const OwnerApprovedItem = ({owner, status, host_id, canLeave}) => {
    const t = useTranslate()

    const [id, prop] = [getId(owner), getType(owner)]

    const {handleAction} = useContext(HostContext)

    const menu = useMemo(
        () =>
            [
                !!canLeave
                    ? {
                        key: HostActionTypes.LEAVE,
                        label: t('leave'),
                        icon: <IoTrashOutline className="text-red-500"/>,
                    }
                    : undefined,
            ].filter((value) => value),
        [t, canLeave]
    )

    return renderElse(
        _.isEmpty(menu),
        <div className="absolute top-5 right-5 contextIcon">
            <DrawMenu
                menu={
                    {
                        items: menu,
                        onClick: ({key}) => {
                            handleAction(key, {
                                id,
                                prop,
                                host_id,
                            })
                        }
                    }
                }
            >
            </DrawMenu>
        </div>
    )
}

const renderItem =
    ({status, request_status, ...item}) =>
        ({params = {}}) => {
            let Component = NullAsDefault
            switch (Number(status)) {
                case -1:
                    Component = PendingItem
                    break
                case -8:
                case -9:
                    Component = RejectedItem
                    break
                default:
                    break
            }
            return (
                <Component
                    {...item}
                    {...params}
                    {...{status}}
                />
            )
        }

const renderOwnerItem =
    ({status, request_status, ...item}) =>
        ({params = {}}) => {
            let Component = NullAsDefault
            switch (Number(status)) {
                case -1:
                    Component = OwnerPendingItem
                    break
                case 1:
                    Component = OwnerApprovedItem
                    break
                default:
                    break
            }
            return (
                <Component
                    {...item}
                    {...params}
                    {...{status}}
                />
            )
        }

export const renderHost =
    ({request_status} = {}) =>
        (item) =>
            (
                <HostContext.Consumer>
                    {({handleAction}) =>
                        renderHostItem(
                            handleAction,
                            Number(request_status) === 1
                                ? undefined
                                : renderItem({
                                    status: _.get(item, 'status'),
                                    request_status,
                                    host_id: getId(item),
                                    owner: _.get(item, 'owner'),
                                })
                        )(item)
                    }
                </HostContext.Consumer>
            )

export const renderHostOwner =
    ({request_status, withoutContextMenu = false} = {}) =>
        (item) => {
            const canLeave = true
            return (
                <HostContext.Consumer>
                    {({handleAction}) =>
                        renderHostRequest(
                            handleAction,
                            withoutContextMenu
                                ? NullAsDefault
                                : Number(request_status) === 1
                                ? undefined
                                : renderOwnerItem({
                                    canLeave,
                                    status: _.get(item, 'status'),
                                    request_status,
                                    host_id: getId(item),
                                    owner: _.get(item, 'owner'),
                                }),
                            {
                                canLeave,
                            }
                        )(item)
                    }
                </HostContext.Consumer>
            )
        }
